import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { restaurantAPI, orderAPI, publicSettingsAPI } from '../../services/api';
import socketService from '../../services/socket';
import { formatDateTimeIST, formatOrderTimeIST } from '../../utils/timezone';
import { useAuth } from '../../context/AuthContext';
import { claimAutoPrint } from '../../utils/printDedup';
import { applyAdminPrintSettings } from '../../utils/adminPrintSettings';

// Toast notification types
const TOAST_TYPES = {
  SUCCESS: 'success',
  INFO: 'info',
  WARNING: 'warning',
  ERROR: 'error'
};

// MED-4 — notification sound URL.
// Previously hardcoded to a Cloudflare R2 bucket with no fallback. If
// the bucket moves or the file is deleted, every restaurant's new-order
// sound silently goes dead. Now env-var driven (REACT_APP_NOTIFICATION_SOUND_URL)
// with a fallback to a locally-bundled asset. The local asset is
// shipped with the CRA build at /sounds/notification.mp3 — place
// it there if you want offline sound (TODO).
const CUSTOM_NOTIFICATION_SOUND_URL =
  process.env.REACT_APP_NOTIFICATION_SOUND_URL ||
  'https://pub-7ccc1a05d7f24ac88e57c3427b104297.r2.dev/sounds/restaurant/sounds-restaurant-1781034058176-2c7b89fc9d68f6a2.mp3';

function RestaurantOrders() {
  const { restaurant } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending'); // Default to 'pending' (New orders) tab
  const [orderTimers, setOrderTimers] = useState({}); // Track time remaining for each pending order
  const [socketConnected, setSocketConnected] = useState(false);
  const [newOrderAlert, setNewOrderAlert] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null); // For order detail modal
  const [showOrderDetail, setShowOrderDetail] = useState(false);
  const [toasts, setToasts] = useState([]); // Toast notification queue
  const [notificationSettings, setNotificationSettings] = useState({ soundEnabled: true }); // Notification preferences
  // HARDCODED: Initialize with custom sound URL directly
const [customSoundUrl, setCustomSoundUrl] = useState('https://pub-7ccc1a05d7f24ac88e57c3427b104297.r2.dev/sounds/restaurant/sounds-restaurant-1781034058176-2c7b89fc9d68f6a2.mp3');
  const [autoAcceptOrders, setAutoAcceptOrders] = useState(true);
  const soundLoopRef = useRef(null);
  const fallbackTimerRef = useRef(null);
  const audioRef = useRef(null);
  const toastIdRef = useRef(0);
  const playNotificationSoundRef = useRef(null); // Ref to always have latest sound function
  const audioUnlockedRef = useRef(false); // Track if audio has been unlocked by user interaction
  const audioContextRef = useRef(null); // Web Audio API context
  const ordersRef = useRef([]); // Ref to access current orders in socket handlers
  const printOrderRef = useRef(null); // Ref to access printOrder function in socket handlers
  const fetchOrdersRef = useRef(null); // Ref to always have latest fetchOrders function
  // Defense-in-depth dedup: tracks every order ID we've processed via socket
  // OR polling OR direct fetch. Guards against rare races where an order can
  // arrive through multiple channels concurrently.
  const processedOrderIdsRef = useRef(new Set());
  // Tracks pending setTimeouts so we can cancel them on unmount (prevents
  // setState-on-unmounted-component warnings).
  const pendingTimeoutsRef = useRef(new Set());
  // Mounted flag for async operations that may resolve after unmount.
  const isMountedRef = useRef(true);

  // Safe setTimeout wrapper that tracks the handle and auto-removes it from
  // the pending set when it fires. Call clearPendingTimeouts() on unmount.
  const safeSetTimeout = useCallback((fn, delay) => {
    const handle = setTimeout(() => {
      pendingTimeoutsRef.current.delete(handle);
      if (isMountedRef.current) fn();
    }, delay);
    pendingTimeoutsRef.current.add(handle);
    return handle;
  }, []);

  const clearPendingTimeouts = useCallback(() => {
    for (const handle of pendingTimeoutsRef.current) {
      clearTimeout(handle);
    }
    pendingTimeoutsRef.current.clear();
  }, []);

  // Track mounted state + cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      clearPendingTimeouts();
    };
  }, [clearPendingTimeouts]);

  // Add toast notification
  const addToast = useCallback((message, type = TOAST_TYPES.INFO, duration = 5000) => {
    const id = ++toastIdRef.current;
    setToasts(prev => [...prev, { id, message, type }]);
    // FRONT-L3: use the tracked safeSetTimeout (guards isMounted + cleared on
    // unmount) instead of a raw setTimeout that fires setState post-unmount.
    safeSetTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, duration);
  }, [safeSetTimeout]);

  // Remove toast
  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  // UNLOCK AUDIO: Browser autoplay policy requires user interaction to enable audio
  // This effect listens for any user interaction and unlocks audio playback
  useEffect(() => {
    const unlockAudio = async () => {
      if (audioUnlockedRef.current) return; // Already unlocked

      console.log('🔓 Unlocking audio on user interaction...');

      try {
        // Create Web Audio API context if not exists
        if (!audioContextRef.current) {
          audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        }

        // Resume audio context if suspended
        if (audioContextRef.current.state === 'suspended') {
          await audioContextRef.current.resume();
          console.log('🔓 AudioContext resumed');
        }

        // Also try to play and immediately pause the audio element to unlock it
        if (audioRef.current) {
          audioRef.current.muted = true;
          try {
            await audioRef.current.play();
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
            audioRef.current.muted = false;
            console.log('🔓 Audio element unlocked');
          } catch (e) {
            console.log('🔓 Audio element unlock attempt:', e.message);
          }
        }

        audioUnlockedRef.current = true;
        console.log('🔓 Audio playback unlocked!');

        // Remove listeners after unlocking
        document.removeEventListener('click', unlockAudio);
        document.removeEventListener('touchstart', unlockAudio);
        document.removeEventListener('keydown', unlockAudio);
      } catch (err) {
        console.log('🔓 Audio unlock error:', err.message);
      }
    };

    // Listen for user interaction to unlock audio
    document.addEventListener('click', unlockAudio);
    document.addEventListener('touchstart', unlockAudio);
    document.addEventListener('keydown', unlockAudio);

    // Also try to unlock immediately in case user already interacted
    unlockAudio();

    return () => {
      document.removeEventListener('click', unlockAudio);
      document.removeEventListener('touchstart', unlockAudio);
      document.removeEventListener('keydown', unlockAudio);
    };
  }, []);

  // Fetch notification settings on mount
  useEffect(() => {
    const fetchNotificationSettings = async () => {
      try {
        const response = await restaurantAPI.getNotificationSettings();
        if (response.data.success && response.data.data.settings) {
          setNotificationSettings(response.data.data.settings);
        }
      } catch (err) {
        console.log('Using default notification settings');
        // Try localStorage fallback
        const saved = localStorage.getItem('restaurantNotificationSettings');
        if (saved) {
          setNotificationSettings(JSON.parse(saved));
        }
      }
    };
    fetchNotificationSettings();
  }, []);

  // Fetch custom notification sound URL from platform settings
  useEffect(() => {
    const fetchCustomSoundSettings = async () => {
      try {
        console.log('🔊 Fetching custom notification sound settings...');
        const response = await publicSettingsAPI.getNotificationSoundSettings();
        console.log('🔊 Notification sound API response:', response.data);

        if (response.data.success && response.data.data) {
          const { customSoundEnabled, restaurantOrderSound } = response.data.data;
          console.log('🔊 customSoundEnabled:', customSoundEnabled, 'restaurantOrderSound:', restaurantOrderSound);

          // Only use custom sound if enabled and URL exists
          if (customSoundEnabled && restaurantOrderSound) {
            console.log('🔊 Setting custom notification sound URL:', restaurantOrderSound);
            setCustomSoundUrl(restaurantOrderSound);
          } else {
            console.log('🔊 Custom sound disabled or no URL set, using default');
          }
        }
      } catch (err) {
        console.log('🔊 Using default notification sound (custom sound fetch failed):', err.message);
      }
    };
    fetchCustomSoundSettings();
  }, []);

  // Fetch auto-accept setting and listen for socket changes
  useEffect(() => {
    const fetchAutoAccept = async () => {
      try {
        const response = await restaurantAPI.getAutoAcceptSetting();
        if (response.data.success) {
          setAutoAcceptOrders(response.data.data.autoAcceptOrders ?? true);
        }
      } catch (err) {
        console.error('Failed to fetch auto-accept setting:', err);
      }
    };
    fetchAutoAccept();

    const handleAutoAcceptChanged = (data) => {
      if (data?.autoAcceptOrders !== undefined) {
        setAutoAcceptOrders(data.autoAcceptOrders);
      }
    };
    const unsubAutoAccept = socketService.on('auto_accept_changed', handleAutoAcceptChanged);

    return () => {
      unsubAutoAccept();
    };
  }, []);

  // FRONT-M2: depend on a derived boolean, not the whole `orders` array. The
  // old `[orders]` dep tore down + recreated the 2s interval on EVERY order
  // update — restarting the alert sound mid-rush. Now it only (re)starts/stops
  // when the pending-ness actually flips.
  const hasPendingOrders = useMemo(() => orders.some(o => o.status === 'pending'), [orders]);

  // Sound loop for pending orders (plays for both auto-accept and manual accept)
  useEffect(() => {
    if (hasPendingOrders) {
      if (!soundLoopRef.current) {
        const playSound = () => {
          // Always use browser audio with R2 CDN URL (auto-updates without app rebuild)
          if (audioRef.current) {
            audioRef.current.volume = 1.0;
            audioRef.current.currentTime = 0;
            audioRef.current.play().catch(() => {});
          }
        };
        playSound();
        soundLoopRef.current = setInterval(playSound, 2000);
      }
    } else {
      if (soundLoopRef.current) {
        clearInterval(soundLoopRef.current);
        soundLoopRef.current = null;
      }
    }

    return () => {
      if (soundLoopRef.current) {
        clearInterval(soundLoopRef.current);
        soundLoopRef.current = null;
      }
    };
  }, [hasPendingOrders]);

  // Reload audio element when custom sound URL changes
  useEffect(() => {
    if (audioRef.current) {
      const newSrc = customSoundUrl || CUSTOM_NOTIFICATION_SOUND_URL;
      console.log('🔊 Setting audio source to:', newSrc);
      // Explicitly set src via ref to ensure it updates
      audioRef.current.src = newSrc;
      audioRef.current.load();
      console.log('🔊 Audio element reloaded with new source');
    }
  }, [customSoundUrl]);

  // Initialize socket connection and listeners
  useEffect(() => {
    // Connect to socket
    socketService.connect();

    // Listen for connection status
    const unsubConnection = socketService.on('connection', (connected) => {
      console.log('🔌 Socket connection status:', connected);
      setSocketConnected(connected);

      if (connected) {
        // Clear fallback polling when socket connects
        if (fallbackTimerRef.current) {
          clearInterval(fallbackTimerRef.current);
          fallbackTimerRef.current = null;
        }
      } else {
        // Start fallback polling when socket disconnects
        startFallbackPolling();
      }
    });

    // Listen for new orders - Use functional state update (official Socket.IO React pattern)
    const unsubNewOrder = socketService.on('new_order', (data) => {
      console.log('🆕 New order received via socket:', data);
      // Show alert for new order
      setNewOrderAlert(data);
      // Play notification sound using ref to get latest function
      if (playNotificationSoundRef.current) {
        playNotificationSoundRef.current();
      }

      // FUNCTIONAL STATE UPDATE: Add new order to state directly (official Socket.IO pattern)
      // This avoids stale closure issues completely
      if (data.order) {
        const incomingId = data.order._id;
        const incomingNumber = data.order.orderNumber;

        // Defense-in-depth dedup across all channels (socket + polling + fetch)
        const alreadyProcessed =
          (incomingId && processedOrderIdsRef.current.has(incomingId)) ||
          (incomingNumber && processedOrderIdsRef.current.has(incomingNumber));
        if (alreadyProcessed) {
          console.log('🆕 Order already processed (cross-channel dedup), skipping:', incomingNumber);
          return;
        }
        if (incomingId) processedOrderIdsRef.current.add(incomingId);
        if (incomingNumber) processedOrderIdsRef.current.add(incomingNumber);

        console.log('🆕 Adding new order to state directly:', incomingNumber);
        setOrders(prevOrders => {
          // Check if order already exists (avoid duplicates)
          const exists = prevOrders.some(o => o._id === incomingId || o.orderNumber === incomingNumber);
          if (exists) {
            console.log('🆕 Order already exists in state, skipping duplicate');
            return prevOrders;
          }
          const newOrders = [data.order, ...prevOrders];
          ordersRef.current = newOrders; // Keep ref in sync
          return newOrders;
        });
      } else {
        // Fallback: If socket data doesn't include full order, fetch from API
        console.log('🆕 No order data in socket event, fetching from API...');
        if (fetchOrdersRef.current) {
          fetchOrdersRef.current();
        }
      }

      // Auto-dismiss alert after 5 seconds (tracked so it's cancelled on unmount)
      safeSetTimeout(() => setNewOrderAlert(null), 5000);
    });

    // Listen for order updates (status changes) - Use functional state update
    const unsubOrderUpdate = socketService.on('order_update', (data) => {
      console.log('📡 Order update received via socket:', data);
      // Show toast for status change
      const statusLabels = {
        pending: 'Pending',
        restaurant_confirmed: 'Confirmed',
        preparing: 'Preparing',
        ready_for_pickup: 'Ready for Pickup',
        assigned_delivery: 'Delivery Assigned',
        picked_up: 'Picked Up',
        on_the_way: 'On the Way',
        delivered: 'Delivered',
        cancelled: 'Cancelled'
      };
      const statusLabel = statusLabels[data.status] || data.status;
      addToast(`Order #${data.orderNumber}: Status changed to ${statusLabel}`, TOAST_TYPES.INFO);

      // FUNCTIONAL STATE UPDATE: Update order status directly in state (official Socket.IO pattern)
      setOrders(prevOrders => {
        const updatedOrders = prevOrders.map(order => {
          if (order._id === data.orderId || order.orderNumber === data.orderNumber) {
            console.log('📡 Updating order status in state:', data.orderNumber, data.previousStatus, '->', data.status);
            // Auto-print when order transitions to 'preparing' (auto-accept)
            if (data.status === 'preparing' && data.previousStatus === 'pending') {
              console.log('🖨️ Auto-printing auto-accepted order:', data.orderNumber);
              // Show notification that order was auto-accepted
              const _ps = JSON.parse(localStorage.getItem('printerSettings') || '{}');
              addToast(`Order #${data.orderNumber} was auto-accepted!${_ps.printEnabled === 'false' ? '' : ' Printing...'}`, TOAST_TYPES.SUCCESS, 6000);
              // Play notification sound
              if (playNotificationSoundRef.current) {
                playNotificationSoundRef.current();
              }
              if (printOrderRef.current) {
                // Use setTimeout to avoid state update during render
                // Pass the current order with isAutoAccept=true so receipt shows "Cashier: Autoaccept"
                safeSetTimeout(() => {
                  if (printOrderRef.current) {
                    printOrderRef.current(order, null, true);
                  }
                }, 500);
              }
            }
            return { ...order, status: data.status };
          }
          return order;
        });
        ordersRef.current = updatedOrders; // Keep ref in sync
        return updatedOrders;
      });
    });

    // Listen for order cancellations - Use functional state update
    const unsubOrderCancelled = socketService.on('order_cancelled', (data) => {
      console.log('❌ Order cancelled via socket:', data);
      addToast(`Order #${data.orderNumber} was cancelled${data.reason ? ': ' + data.reason : ''}`, TOAST_TYPES.WARNING);
      if (playNotificationSoundRef.current) {
        playNotificationSoundRef.current();
      }

      // FUNCTIONAL STATE UPDATE: Update order status to cancelled
      setOrders(prevOrders => {
        const updatedOrders = prevOrders.map(order => {
          if (order._id === data.orderId || order.orderNumber === data.orderNumber) {
            console.log('❌ Updating order to cancelled in state:', data.orderNumber);
            return { ...order, status: 'cancelled' };
          }
          return order;
        });
        ordersRef.current = updatedOrders; // Keep ref in sync
        return updatedOrders;
      });
    });

    // Listen for order assignment (delivery partner assigned) - Use functional state update
    const unsubOrderAssigned = socketService.on('order_assigned', (data) => {
      console.log('🚴 Order assigned via socket:', data);
      addToast(`Order #${data.orderNumber}: Delivery partner ${data.deliveryPartnerName || ''} assigned`, TOAST_TYPES.SUCCESS);
      if (playNotificationSoundRef.current) {
        playNotificationSoundRef.current();
      }

      // FUNCTIONAL STATE UPDATE: Update order with delivery partner info
      setOrders(prevOrders => {
        const updatedOrders = prevOrders.map(order => {
          if (order._id === data.orderId || order.orderNumber === data.orderNumber) {
            console.log('🚴 Updating order with delivery assignment in state:', data.orderNumber);
            return {
              ...order,
              status: data.status || 'assigned_delivery',
              deliveryPartnerId: data.deliveryPartnerId || order.deliveryPartnerId
            };
          }
          return order;
        });
        ordersRef.current = updatedOrders; // Keep ref in sync
        return updatedOrders;
      });
    });

    // Listen for payment confirmation - Use functional state update
    const unsubPaymentConfirmed = socketService.on('payment_confirmed', (data) => {
      console.log('💳 Payment confirmed via socket:', data);
      addToast(`Order #${data.orderNumber}: Payment of ₹${data.total} confirmed`, TOAST_TYPES.SUCCESS);
      if (playNotificationSoundRef.current) {
        playNotificationSoundRef.current();
      }

      // FUNCTIONAL STATE UPDATE: Update order payment status
      setOrders(prevOrders => {
        const updatedOrders = prevOrders.map(order => {
          if (order._id === data.orderId || order.orderNumber === data.orderNumber) {
            console.log('💳 Updating order payment status in state:', data.orderNumber);
            return { ...order, paymentStatus: 'received' };
          }
          return order;
        });
        ordersRef.current = updatedOrders; // Keep ref in sync
        return updatedOrders;
      });
    });

    // Listen for new reviews
    const unsubReviewReceived = socketService.on('review_received', (data) => {
      console.log('⭐ Review received:', data);
      const stars = data.restaurantRating ? '★'.repeat(Math.round(data.restaurantRating)) : '';
      addToast(`New Review ${stars}: ${data.customerName || 'Customer'} left a ${data.restaurantRating}/5 rating`, TOAST_TYPES.INFO, 8000);
      if (playNotificationSoundRef.current) {
        playNotificationSoundRef.current();
      }
    });

    // Join restaurant room for real-time updates
    if (restaurant?._id) {
      socketService.joinRestaurantRoom(restaurant._id);
    }

    return () => {
      // Cleanup socket listeners
      unsubConnection();
      unsubNewOrder();
      unsubOrderUpdate();
      unsubOrderCancelled();
      unsubOrderAssigned();
      unsubPaymentConfirmed();
      unsubReviewReceived();
      // Clear fallback timer and null the ref so a re-mount can restart it
      if (fallbackTimerRef.current) {
        clearInterval(fallbackTimerRef.current);
        fallbackTimerRef.current = null;
      }
    };
  }, [restaurant, addToast, safeSetTimeout]); // Using playNotificationSoundRef instead of playNotificationSound

  const startFallbackPolling = () => {
    if (!fallbackTimerRef.current) {
      console.log('📡 Starting fallback polling (socket disconnected)');
      // Use ref wrapper to avoid stale closure - fetchOrdersRef.current always has latest function
      fallbackTimerRef.current = setInterval(() => {
        if (fetchOrdersRef.current) {
          fetchOrdersRef.current();
        }
      }, 15000);
    }
  };

  const playNotificationSound = useCallback(async () => {
    console.log('🔊 playNotificationSound() called');
    console.log('🔊 soundEnabled:', notificationSettings.soundEnabled);

    // Check if sound is enabled
    if (!notificationSettings.soundEnabled) {
      console.log('🔇 Sound disabled in settings');
      return;
    }

    const soundUrl = customSoundUrl || CUSTOM_NOTIFICATION_SOUND_URL;
    console.log('🔊 Playing sound:', soundUrl);

    // Method 1: Try new Audio object (doesn't require CORS for playback)
    try {
      const audio = new Audio(soundUrl);
      audio.volume = 1.0;
      await audio.play();
      console.log('🔊 Playing via new Audio() - SUCCESS!');
      return;
    } catch (err) {
      console.log('🔊 new Audio() failed:', err.message);
    }

    // Method 2: Try HTML5 Audio element ref
    if (audioRef.current) {
      try {
        audioRef.current.src = soundUrl;
        audioRef.current.currentTime = 0;
        audioRef.current.volume = 1.0;
        await audioRef.current.play();
        console.log('🔊 Playing via audioRef - SUCCESS!');
        return;
      } catch (err) {
        console.log('🔊 audioRef.play() failed:', err.message);
      }
    }

    console.log('🔊 All audio methods failed');
  }, [notificationSettings.soundEnabled, customSoundUrl]);

  // Keep ref updated with latest playNotificationSound function
  useEffect(() => {
    console.log('🔊 DEBUG: Setting playNotificationSoundRef.current to playNotificationSound');
    playNotificationSoundRef.current = playNotificationSound;
  }, [playNotificationSound]);

  useEffect(() => {
    fetchOrders();
  }, []);

  // Timer to update countdown every second
  // Only show timer for COD orders or online orders with confirmed payments
  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date().getTime();
      const newTimers = {};

      orders.forEach(order => {
        if (order.status === 'pending') {
          // Only start timer for COD orders or orders with confirmed payment
          const shouldShowTimer = order.paymentMethod === 'cash' || order.paymentStatus === 'received';

          if (shouldShowTimer) {
            // Use paymentConfirmedAt for timer calculation (works for both COD and online orders)
            const timerStartTime = order.paymentConfirmedAt
              ? new Date(order.paymentConfirmedAt).getTime()
              : new Date(order.createdAt).getTime();

            const elapsedSeconds = Math.floor((now - timerStartTime) / 1000);
            const remainingSeconds = Math.max(0, 30 - elapsedSeconds);
            newTimers[order._id] = remainingSeconds;
          }
        }
      });

      setOrderTimers(newTimers);
    }, 1000); // Update every second

    return () => clearInterval(interval);
  }, [orders]);

  const fetchOrders = async () => {
    try {
      const response = await restaurantAPI.getMyOrders();
      const newOrders = response.data.data.orders;
      if (!isMountedRef.current) return; // Avoid setState on unmounted component
      setOrders(newOrders);
      ordersRef.current = newOrders; // Keep ref in sync for socket handlers
      // Seed cross-channel dedup set with fetched IDs so a subsequent socket
      // new_order event for an already-fetched order is correctly skipped.
      for (const o of newOrders) {
        if (o._id) processedOrderIdsRef.current.add(o._id);
        if (o.orderNumber) processedOrderIdsRef.current.add(o.orderNumber);
      }
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  };

  // Keep fetchOrdersRef updated with latest fetchOrders function to avoid stale closures in socket callbacks
  useEffect(() => {
    fetchOrdersRef.current = fetchOrders;
  });

  const updateOrderStatus = async (orderId, status, orderToPrint = null) => {
    try {
      await orderAPI.updateStatus(orderId, status);

      // Auto-print when order is accepted (status = 'preparing')
      if (status === 'preparing' && orderToPrint) {
        console.log('🖨️ Auto-printing order after acceptance...');
        // Print immediately - no delay, no blocking alert
        printOrder(orderToPrint);
      }

      // Use toast instead of blocking alert
      addToast('Order status updated!', TOAST_TYPES.SUCCESS);
      fetchOrders();
    } catch (error) {
      addToast('Failed to update order status', TOAST_TYPES.ERROR);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: '#F59E0B',
      restaurant_confirmed: '#3B82F6',
      preparing: '#8B5CF6',
      ready_for_pickup: '#06B6D4',
      assigned_delivery: '#22C55E',
      picked_up: '#22C55E',
      on_the_way: '#22C55E',
      delivered: '#10B981',
      cancelled: '#EF4444'
    };
    return colors[status] || '#6B7280';
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: '⏳',
      restaurant_confirmed: '✅',
      preparing: '👨‍🍳',
      ready_for_pickup: '📦',
      assigned_delivery: '🚴',
      picked_up: '📦',
      on_the_way: '🛵',
      delivered: '✅',
      cancelled: '❌'
    };
    return icons[status] || '📋';
  };

  const openOrderDetail = (order) => {
    setSelectedOrder(order);
    setShowOrderDetail(true);
  };

  const closeOrderDetail = () => {
    setShowOrderDetail(false);
    setSelectedOrder(null);
  };

  // Print Order Function - Uses printer settings from localStorage
  // Uses Electron silent print or iframe-based printing
  const printOrder = (order, e, isAutoAccept = false, isManualReprint = false) => {
    if (e) e.stopPropagation();

    // Adopt any admin-pushed print settings (once per admin update) BEFORE reading
    // the local settings, so printing uses the latest admin-managed format.
    applyAdminPrintSettings(restaurant);

    // Load printer settings from localStorage
    const defaultSettings = { paperWidth: '58', paperHeight: '150', fontSize: '9', fontWeight: 'bold', lineHeight: '1.15' };
    let printerSettings = defaultSettings;
    try {
      const saved = localStorage.getItem('printerSettings');
      if (saved) printerSettings = { ...defaultSettings, ...JSON.parse(saved) };
    } catch (e) { console.error('Failed to load printer settings:', e); }

    // Check if printing is disabled — manual reprint always allowed
    if (printerSettings.printEnabled === 'false' && !isManualReprint) {
      console.log('🖨️ Printing disabled — skipping auto receipt print');
      return;
    }

    // #9: dedup auto-prints across Dashboard+Orders and across reconnects/retried
    // webhooks. Manual reprints bypass. claimAutoPrint atomically marks the order.
    if (!isManualReprint) {
      if (!claimAutoPrint(order?._id || order?.id)) {
        console.log('🖨️ Skipping duplicate auto-print for order', order?.orderNumber);
        return;
      }
    }

    // Get restaurant details from auth context (restaurant from useAuth)
    // Note: order.restaurantId is just an ObjectId, not populated data
    const restaurantData = restaurant || {};
    const restaurantName = restaurantData.name || 'Restaurant';
    const restaurantAddress = restaurantData.address?.street || restaurantData.address?.fullAddress || '';
    const restaurantCity = restaurantData.address?.city || '';
    const restaurantState = restaurantData.address?.state || '';
    const restaurantPincode = restaurantData.address?.zipCode || '';
    // GST and FSSAI are nested under documents
    const restaurantGST = restaurantData.documents?.gst?.number || '';
    const restaurantPhone = restaurantData.contactPhone || restaurantData.phone || '';
    const restaurantFSSAI = restaurantData.documents?.fssai?.number || '';
    const isPaid = order.paymentMethod !== 'cash';

    // Calculate totals - ensure all values are valid numbers
    const subtotal = Number(order.pricing?.subtotal) || 0;
    // Under Section 9(5), restaurantEarnings.gstCollected is 0 (ECO pays GST)
    // Use pricing.tax (actual GST collected from customer) or platformEarnings.section9_5GstCollected as fallback
    const gstCollected = Number(order.earnings?.restaurantEarnings?.gstCollected) ||
                         Number(order.pricing?.tax) ||
                         Number(order.earnings?.platformEarnings?.section9_5GstCollected) || 0;
    const gstRate = Number(order.earnings?.restaurantEarnings?.gstRate) || 5;
    const cgstRate = gstRate / 2;
    const sgstRate = gstRate / 2;
    const cgstAmount = gstCollected / 2;
    const sgstAmount = gstCollected / 2;
    // Grand total = what customer pays for food (subtotal + GST - discount)
    // NOT restaurantEarnings.netPayable (that's restaurant's take after commission/TDS)
    const discount = Number(order.pricing?.discount) || 0;
    const grandTotal = subtotal + gstCollected - discount;
    const roundedTotal = Math.round(grandTotal);
    const roundOff = roundedTotal - grandTotal;
    const totalQty = (order.items || []).reduce((sum, item) => sum + (Number(item.quantity) || 1), 0);

    // Format date
    const orderDate = order.createdAt ? new Date(order.createdAt) : new Date();
    const formattedDate = orderDate.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: '2-digit' });

    // Build delivery address - Order model has: street, city, state, zipCode
    const delAddr = order.deliveryAddress ?
      [order.deliveryAddress.street, order.deliveryAddress.city, order.deliveryAddress.state, order.deliveryAddress.zipCode]
        .filter(Boolean).join(', ') || order.deliveryAddress.fullAddress || '' : '';

    // Build full receipt - FIXED character width for identical output on ALL platforms.
    // Courier New monospace: exactly 0.6 × fontSize in Chromium's HarfBuzz text shaper.
    // This value is the same on macOS, Windows, and Linux because Chromium uses
    // the same text layout engine everywhere. DO NOT use DOM measurement — it
    // returns different values per platform due to different font rasterizers,
    // which produces different W and therefore different receipt layouts.
    // Paper pixel widths at 203 DPI: 58mm=384px, 80mm=576px
    const _paperPx = printerSettings.paperWidth === '80' ? 576 : 384;
    const _ml = parseInt(printerSettings.marginLeft || '2') || 2;
    const _mr = parseInt(printerSettings.marginRight || '2') || 2;
    const _charWidth = parseInt(printerSettings.fontSize || '9') * 0.6;
    const W = Math.max(20, Math.floor((_paperPx - _ml - _mr) / _charWidth));
    const line = '='.repeat(W);
    const dash = '-'.repeat(W);
    const pad = (t) => { const s = String(t); return ' '.repeat(Math.max(0, Math.floor((W - s.length) / 2))) + s; };
    // Helper: right-align a value on a line (label left, value flush right)
    // If label+value doesn't fit, put value on next line right-aligned
    const rAlign = (label, val) => {
      const v = String(val);
      const l = String(label);
      if (l.length + v.length + 1 > W) {
        // Doesn't fit - wrap label, value on next line right-aligned
        return l + '\n' + v.padStart(W);
      }
      const gap = W - l.length - v.length;
      return l + ' '.repeat(gap) + v;
    };
    // Helper: wrap a line to fit within W chars, indenting continuation lines
    const wrapLine = (text, indent = 2) => {
      if (text.length <= W) return text + '\n';
      let out = text.substring(0, W) + '\n';
      let rem = text.substring(W);
      const contW = W - indent;
      while (rem.length > 0) {
        out += ' '.repeat(indent) + rem.substring(0, contW) + '\n';
        rem = rem.substring(contW);
      }
      return out;
    };
    // Helper: wrap text and center each line (for header/footer)
    const padWrap = (text) => {
      if (text.length <= W) return pad(text) + '\n';
      let out = '';
      let rem = text;
      while (rem.length > 0) {
        out += pad(rem.substring(0, W)) + '\n';
        rem = rem.substring(W);
      }
      return out;
    };

    // Build items text - adapt column widths to paper size
    // 58mm: name(14) + qty(3) + price(7) + amt(8) = 32
    // 80mm: name(18) + qty(3) + price(9) + amt(12) = 42
    const is58 = W <= 32;
    const colQty = 3;
    const colPrice = is58 ? 7 : 9;
    const colAmt = is58 ? 8 : 12;
    const nameW = W - colQty - colPrice - colAmt;
    let itemsText = '';
    (order.items || []).forEach(item => {
      const qty = Number(item.quantity) || 1;
      const price = Number(item.price) || 0;
      const amt = qty * price;
      const fullName = item.name || 'Item';
      const numsPart = String(qty).padStart(colQty) + String(price.toFixed(0)).padStart(colPrice) + String(amt.toFixed(0)).padStart(colAmt);
      if (fullName.length <= nameW) {
        // Name fits in column - single line
        itemsText += fullName.padEnd(nameW) + numsPart + '\n';
      } else {
        // Name too long - full name on first line, numbers on next line right-aligned
        itemsText += fullName.substring(0, W) + '\n';
        itemsText += ''.padEnd(nameW) + numsPart + '\n';
      }
      if (item.variant) itemsText += '  ' + item.variant.substring(0, W - 2) + '\n';
      if (item.addons && item.addons.length > 0) {
        itemsText += '  +' + item.addons.map(a => a.name || '').join(',').substring(0, W - 3) + '\n';
      }
    });

    // Build GST text (amounts right-aligned)
    let gstText = '';
    if (gstCollected > 0) {
      gstText = rAlign(subtotal.toFixed(2) + '@ ' + cgstRate + '%CGST', cgstAmount.toFixed(2)) + '\n' +
                rAlign(subtotal.toFixed(2) + '@ ' + sgstRate + '%SGST', sgstAmount.toFixed(2)) + '\n';
    }

    let r = '';
    r += '<div style="text-align:center;font-size:' + (parseInt(printerSettings.fontSize) + 4) + 'px;font-weight:bold;word-wrap:break-word;max-width:100%;margin:0;padding:0">' + restaurantName + '</div><pre>';
    if (isPaid) r += padWrap('PAID');
    if (restaurantAddress) r += padWrap('Add.:- ' + restaurantAddress);
    if (restaurantCity || restaurantPincode) r += padWrap(restaurantCity + '- ' + restaurantPincode);
    if (restaurantGST) r += padWrap('GST NO. ' + restaurantGST);
    if (restaurantPhone) r += padWrap('Ph. ' + restaurantPhone);
    r += line + '\n';
    r += padWrap('INVOICE');
    r += line + '\n';
    r += wrapLine('From Foodwinc[' + order.orderNumber + ']');
    r += wrapLine('Name: ' + (order.userId?.name || 'Customer'));
    if (delAddr) r += wrapLine('Adr: ' + delAddr);
    r += dash + '\n';
    r += wrapLine(rAlign('Date: ' + formattedDate, 'Delivery'));
    r += wrapLine((isAutoAccept ? 'Cashier: Autoaccept  ' : '') + 'Bill No.: ' + order.orderNumber);
    r += dash + '\n';
    r += 'Item'.padEnd(nameW) + 'Qty'.padStart(colQty) + 'Price'.padStart(colPrice) + 'Amount'.padStart(colAmt) + '\n';
    r += dash + '\n';
    r += itemsText;
    r += dash + '\n';
    r += rAlign('Total Qty: ' + totalQty, 'Sub Total ' + subtotal.toFixed(2)) + '\n';
    r += gstText;
    if (discount > 0) r += rAlign('Discount', '-' + discount.toFixed(2)) + '\n';
    if (Math.abs(roundOff) >= 0.01) r += rAlign('Round off', roundOff.toFixed(2)) + '\n';
    r += line + '\n';
    r += '<b>' + rAlign('Grand Total', 'Rs.' + roundedTotal.toFixed(0)) + '</b>\n';
    r += line + '\n';
    r += wrapLine('Paid via ' + (order.paymentMethod === 'cash' ? 'Cash on Delivery' : 'Online [Foodwinc]'));
    if (order.specialInstructions) { r += dash + '\n'; r += wrapLine('Customer Notes: ' + order.specialInstructions); }
    r += dash + '\n';
    // Section 9(5) Notice - ECO is deemed supplier
    r += wrapLine('Tax to be paid under section 9(5) by Eco');
    r += dash + '\n';
    if (restaurantFSSAI) r += padWrap('FSSAI Lic No. ' + restaurantFSSAI);
    r += padWrap('NO EXCHANGE NO RETURN.');
    r += padWrap('--Thank You, Visit Again-- :)');

    // HTML for thermal printer - uses settings from localStorage
    const pw = printerSettings.paperWidth;
    const ph = printerSettings.paperHeight;
    const fs = printerSettings.fontSize;
    const fw = printerSettings.fontWeight;
    const lh = printerSettings.lineHeight;
    const bodyWidth = parseInt(pw) - 4; // subtract padding
    // Get selected printer for data attribute
    let _selPrinter = '';
    try { _selPrinter = localStorage.getItem('selectedPrinter') || ''; } catch (_) {}

    const _printerType = printerSettings.printerType || 'escpos';
    const _printDir = printerSettings.printDirection || '1';
    const _printDensity = printerSettings.printDensity || '8';
    const _printSpeed = printerSettings.printSpeed || '4';
    const ml = printerSettings.marginLeft || '2';
    const mr = printerSettings.marginRight || '2';
    const printContent = '<!DOCTYPE html><html data-printer="' + _selPrinter + '" data-paper="' + pw + '" data-printer-type="' + _printerType + '" data-direction="' + _printDir + '" data-density="' + _printDensity + '" data-speed="' + _printSpeed + '" data-margin-bottom="' + (printerSettings.marginBottom || '0') + '" data-raster-band="' + (printerSettings.rasterBandHeight || '0') + '"><head><meta charset="utf-8"><title>Invoice</title>' +
      '<style>' +
      '@page{margin:0!important;padding:0!important}' +
      'html{margin:0!important;padding:0!important}' +
      '*{box-sizing:border-box;page-break-inside:avoid!important;break-inside:avoid!important;-webkit-print-color-adjust:exact!important}' +
      'body{margin:0!important;padding:0 ' + mr + 'px 0 ' + ml + 'px;font-family:ReceiptMono,"Courier New",Courier,monospace;font-size:' + fs + 'px;font-weight:' + fw + ';line-height:' + lh + ';width:100%}' +
      'pre{margin:0;padding:0;white-space:pre-wrap;word-wrap:break-word}' +
      '</style></head><body>' + r + '</pre></body></html>';

    // Electron print — try server-generated image first (identical on ALL devices),
    // fall back to local HTML rendering if API fails (offline mode).
    if (window.electronAPI?.printReceiptImage && order._id) {
      const apiBase = window.location.origin.replace(':3001', ':3000').replace(':3002', ':3000');
      const qs = `paperWidth=${pw}&fontSize=${fs}&fontWeight=${fw}&lineHeight=${lh}&marginLeft=${ml}&marginRight=${mr}`;
      const imageUrl = `${apiBase}/api/orders/${order._id}/receipt-image?${qs}`;
      const token = localStorage.getItem('token') || '';
      console.log('🖨️ Printing via server image →', _selPrinter || 'default');
      const printSettings = {
        printerName: _selPrinter, printerType: _printerType, paperWidth: pw,
        printDirection: _printDir, printDensity: _printDensity, printSpeed: _printSpeed,
        marginBottom: printerSettings.marginBottom || '0',
      };
      // "Print only KOT" mode — skip the customer receipt and print JUST the
      // kitchen ticket (through the same server-image primitive). kotOnly wins
      // over kotEnabled: the whole point of this mode is to print the KOT alone.
      if (printerSettings.kotOnly === 'true') {
        const kotOnlyUrl = `${apiBase}/api/orders/${order._id}/kot-image?${qs}`;
        console.log('🖨️ KOT-only mode → printing kitchen ticket, skipping receipt');
        window.electronAPI.printReceiptImage(kotOnlyUrl, printSettings, token)
          .then(result => {
            if (!result.success && result.stage !== 'print') {
              console.warn('🖨️ KOT-only print issue (no local fallback, avoids duplicate):', result.error);
              addToast('KOT sent to printer. If nothing printed, check the printer.', TOAST_TYPES.INFO);
            }
          })
          .catch(err => {
            console.error('🖨️ KOT-only print error:', err);
            addToast('Print failed. Please try again.', TOAST_TYPES.ERROR);
          });
        return;
      }
      window.electronAPI.printReceiptImage(imageUrl, printSettings, token)
        .then(result => {
          if (result.success) {
            console.log('🖨️ Receipt printed via server image:', result.printer);
          } else if (result.stage === 'print') {
            // Printer-stage failure (1.0.14+ reports the stage): the raster already
            // reached the SAME printer, so do NOT fall back to a local HTML print —
            // that would DUPLICATE the receipt on a slow-printer false-negative, or
            // double-fail. The local HTML path uses the same printer anyway, so a
            // fallback could never help here. Surface a soft notice instead.
            console.warn('🖨️ Receipt printer-stage issue (no local fallback, avoids duplicate):', result.error);
            addToast('Receipt sent to printer. If nothing printed, check the printer.', TOAST_TYPES.INFO);
          } else {
            // Fetch/render failure (offline / server image unavailable): local HTML print.
            console.warn('🖨️ Server image unavailable, falling back to local HTML:', result.error);
            window.electronAPI.silentPrint(printContent).then(r2 => {
              if (!r2.success) addToast('Print failed: ' + r2.error, TOAST_TYPES.ERROR);
            }).catch(() => addToast('Print failed. Please try again.', TOAST_TYPES.ERROR));
          }
          // Print KOT slip if enabled
          if (printerSettings.kotEnabled !== 'false' && order._id) {
            const kotQs = `paperWidth=${pw}&fontSize=${fs}&fontWeight=${fw}&lineHeight=${lh}&marginLeft=${ml}&marginRight=${mr}`;
            const kotUrl = `${apiBase}/api/orders/${order._id}/kot-image?${kotQs}`;
            setTimeout(() => {
              window.electronAPI.printReceiptImage(kotUrl, printSettings, token)
                .then(r => { if (!r.success) console.warn('🖨️ KOT print failed:', r.error); })
                .catch(() => {});
            }, 1000); // 1s delay — must exceed 0.5s print debounce timer
          }
        })
        .catch(err => {
          console.error('🖨️ Print error:', err);
          addToast('Print failed. Please try again.', TOAST_TYPES.ERROR);
        });
      return;
    }

    // Fallback: iframe-based printing for browser
    console.log('🖨️ Using iframe print (browser mode)...');
    try {
      // Remove any existing print iframe
      const existingIframe = document.getElementById('print-iframe');
      if (existingIframe) {
        existingIframe.remove();
      }

      // Create hidden iframe for printing
      const iframe = document.createElement('iframe');
      iframe.id = 'print-iframe';
      iframe.style.position = 'absolute';
      iframe.style.width = '58mm';
      iframe.style.height = '150mm';
      iframe.style.border = 'none';
      iframe.style.left = '-9999px';
      document.body.appendChild(iframe);

      // Write content to iframe
      const iframeDoc = iframe.contentWindow || iframe.contentDocument;
      const doc = iframeDoc.document || iframeDoc;
      doc.open();
      doc.write(printContent);
      doc.close();

      // Wait for content to fully render then print (Chrome/Chromium timing fix)
      const doPrint = () => {
        try {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
          console.log('🖨️ Print dialog opened successfully');

          // Clean up iframe after a delay (give time for print dialog)
          setTimeout(() => {
            if (iframe.parentNode) {
              iframe.parentNode.removeChild(iframe);
            }
          }, 2000);
        } catch (printError) {
          console.error('🖨️ Print failed:', printError);
          addToast('Failed to print order. Please try again.', TOAST_TYPES.ERROR);
          if (iframe.parentNode) {
            iframe.parentNode.removeChild(iframe);
          }
        }
      };

      // Critical: Add delay to ensure content is rendered before print
      // Chrome/Chromium fires onload before content is visually ready
      iframe.onload = () => {
        setTimeout(doPrint, 500);
      };

      // Also trigger after a delay if onload doesn't fire
      setTimeout(() => {
        if (iframe.parentNode) {
          doPrint();
        }
      }, 800);
    } catch (error) {
      console.error('🖨️ Print setup failed:', error);
      addToast('Failed to print order. Please try again.', TOAST_TYPES.ERROR);

      // Fallback: try window.open method (works when triggered by user click)
      try {
        const printWindow = window.open('', '_blank');
        if (printWindow) {
          printWindow.document.write(printContent);
          printWindow.document.close();
          printWindow.onload = function() {
            printWindow.print();
          };
        } else {
          console.error('🖨️ Popup blocked - cannot print');
          addToast('Print blocked. Please click "Print Order" button manually.', TOAST_TYPES.WARNING);
        }
      } catch (fallbackError) {
        console.error('🖨️ Fallback print also failed:', fallbackError);
      }
    }
  };

  // FRONT-M1: sync printOrderRef in an effect, not during render (render-phase
  // ref mutation breaks under React 18 concurrent / StrictMode).
  useEffect(() => { printOrderRef.current = printOrder; });

  // Order Detail Modal Component
  const OrderDetailModal = ({ order, onClose }) => {
    if (!order) return null;

    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px'
      }} onClick={onClose}>
        <div style={{
          background: 'white',
          borderRadius: '16px',
          maxWidth: '600px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
        }} onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div style={{
            background: 'linear-gradient(135deg, #FF6B6B, #FF8E8E)',
            padding: '20px',
            borderRadius: '16px 16px 0 0',
            color: 'white'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '700' }}>
                  Order #{order.orderNumber}
                </h2>
                <p style={{ margin: '4px 0 0', opacity: 0.9, fontSize: '14px' }}>
                  {formatDateTimeIST(order.createdAt)}
                </p>
              </div>
              <button onClick={onClose} style={{
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                color: 'white',
                fontSize: '20px',
                cursor: 'pointer'
              }}>×</button>
            </div>
            <div style={{
              marginTop: '12px',
              padding: '8px 16px',
              background: 'rgba(255,255,255,0.2)',
              borderRadius: '20px',
              display: 'inline-block',
              fontSize: '12px',
              fontWeight: '600',
              textTransform: 'uppercase'
            }}>
              {getStatusIcon(order.status)} {order.status.replace(/_/g, ' ')}
            </div>
          </div>

          {/* Content */}
          <div style={{ padding: '20px' }}>
            {/* Customer Info */}
            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ fontSize: '14px', color: '#64748B', marginBottom: '12px', fontWeight: '600' }}>
                👤 CUSTOMER DETAILS
              </h3>
              <div style={{
                background: '#F8FAFC',
                padding: '16px',
                borderRadius: '12px'
              }}>
                <p style={{ margin: '0 0 8px', fontWeight: '600', color: '#1E293B' }}>
                  {order.userId?.name || 'Unknown Customer'}
                </p>
                <p style={{ margin: '0 0 4px', fontSize: '14px', color: '#64748B' }}>
                  📞 {order.userId?.phone || 'N/A'}
                </p>
                {order.userId?.email && (
                  <p style={{ margin: 0, fontSize: '14px', color: '#64748B' }}>
                    ✉️ {order.userId.email}
                  </p>
                )}
              </div>
            </div>

            {/* Delivery Address */}
            {order.deliveryAddress && (
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ fontSize: '14px', color: '#64748B', marginBottom: '12px', fontWeight: '600' }}>
                  📍 DELIVERY ADDRESS
                </h3>
                <div style={{
                  background: '#F8FAFC',
                  padding: '16px',
                  borderRadius: '12px'
                }}>
                  <p style={{ margin: 0, fontSize: '14px', color: '#1E293B' }}>
                    {order.deliveryAddress.fullAddress ||
                     `${order.deliveryAddress.addressLine1 || ''} ${order.deliveryAddress.addressLine2 || ''}`}
                  </p>
                  {order.deliveryAddress.landmark && (
                    <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#64748B' }}>
                      Landmark: {order.deliveryAddress.landmark}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Order Items */}
            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ fontSize: '14px', color: '#64748B', marginBottom: '12px', fontWeight: '600' }}>
                🍽️ ORDER ITEMS ({order.items?.length || 0})
              </h3>
              <div style={{
                background: '#F8FAFC',
                borderRadius: '12px',
                overflow: 'hidden'
              }}>
                {order.items?.map((item, index) => {
                  const qty = Number(item.quantity) || 1;
                  // "{qty} x {name}" + variant + addons, all on one bold line.
                  let label = item.name || 'Item';
                  if (item.variant) label += ` ${item.variant}`;
                  if (item.addons?.length > 0) {
                    const addons = item.addons
                      .map(a => (typeof a === 'string' ? a : (a && a.name) || ''))
                      .filter(Boolean);
                    if (addons.length) label += ` + ${addons.join(', ')}`;
                  }
                  // Line total (price of the FULL quantity). Prefer the stored
                  // subtotal (variant/addon-accurate); else unit × qty.
                  const lineTotal = (item.subtotal != null && Number(item.subtotal) > 0)
                    ? Number(item.subtotal)
                    : (Number(item.price) || 0) * qty;
                  return (
                    <div key={index} style={{
                      padding: '12px 16px',
                      borderBottom: index < order.items.length - 1 ? '1px solid #E2E8F0' : 'none',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      gap: '12px'
                    }}>
                      <p style={{ margin: 0, fontWeight: 600, color: '#1E293B', lineHeight: 1.4 }}>
                        {qty} x {label}
                      </p>
                      <p style={{ margin: 0, fontWeight: 600, color: '#1E293B', whiteSpace: 'nowrap' }}>
                        ₹{lineTotal.toFixed(0)}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Special Instructions */}
            {order.specialInstructions && (
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ fontSize: '14px', color: '#64748B', marginBottom: '12px', fontWeight: '600' }}>
                  📝 SPECIAL INSTRUCTIONS
                </h3>
                <div style={{
                  background: '#FEF3C7',
                  padding: '12px 16px',
                  borderRadius: '12px',
                  border: '1px solid #FCD34D'
                }}>
                  <p style={{ margin: 0, fontSize: '14px', color: '#92400E' }}>
                    {order.specialInstructions}
                  </p>
                </div>
              </div>
            )}

            {/* Payment Details */}
            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ fontSize: '14px', color: '#64748B', marginBottom: '12px', fontWeight: '600' }}>
                💳 PAYMENT DETAILS
              </h3>
              <div style={{
                background: '#F8FAFC',
                padding: '16px',
                borderRadius: '12px'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ color: '#64748B' }}>Subtotal</span>
                  <span style={{ fontWeight: '500' }}>₹{order.pricing?.subtotal || 0}</span>
                </div>
                {order.earnings?.restaurantEarnings?.gstCollected > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span style={{ color: '#64748B' }}>Restaurant GST ({order.earnings?.restaurantEarnings?.gstRate || 5}%)</span>
                    <span style={{ fontWeight: '500' }}>₹{order.earnings.restaurantEarnings.gstCollected.toFixed(1)}</span>
                  </div>
                )}
                {order.pricing?.discount > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span style={{ color: '#22C55E' }}>Discount</span>
                    <span style={{ fontWeight: '500', color: '#22C55E' }}>-₹{order.pricing.discount}</span>
                  </div>
                )}
                {order.earnings?.restaurantEarnings?.tdsDeducted > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span style={{ color: '#EF4444' }}>TDS Deducted ({order.earnings?.restaurantEarnings?.tdsRate || 0.1}%)</span>
                    <span style={{ fontWeight: '500', color: '#EF4444' }}>-₹{order.earnings.restaurantEarnings.tdsDeducted.toFixed(1)}</span>
                  </div>
                )}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  paddingTop: '12px',
                  borderTop: '1px dashed #E2E8F0',
                  marginTop: '8px'
                }}>
                  <span style={{ fontWeight: '700', color: '#1E293B' }}>Net Payable</span>
                  <span style={{ fontWeight: '700', fontSize: '18px', color: '#FF6B6B' }}>
                    ₹{order.earnings?.restaurantEarnings?.netPayable > 0
                      ? order.earnings.restaurantEarnings.netPayable.toFixed(0)
                      : ((order.pricing?.subtotal || 0) + (order.earnings?.restaurantEarnings?.gstCollected || 0)).toFixed(0)}
                  </span>
                </div>
                <div style={{
                  marginTop: '12px',
                  padding: '8px 12px',
                  background: order.paymentMethod === 'cash' ? '#FEF3C7' : '#DCFCE7',
                  borderRadius: '8px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <span style={{ fontSize: '13px', color: order.paymentMethod === 'cash' ? '#92400E' : '#166534' }}>
                    {order.paymentMethod === 'cash' ? '💵 Cash on Delivery' : '💳 Online Payment'}
                  </span>
                  <span style={{
                    fontSize: '12px',
                    fontWeight: '600',
                    padding: '2px 8px',
                    borderRadius: '12px',
                    background: order.paymentStatus === 'received' ? '#22C55E' : '#F59E0B',
                    color: 'white'
                  }}>
                    {order.paymentStatus || 'pending'}
                  </span>
                </div>
              </div>
            </div>

            {/* Delivery Partner */}
            {order.deliveryPartnerId && (
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ fontSize: '14px', color: '#64748B', marginBottom: '12px', fontWeight: '600' }}>
                  🚴 DELIVERY PARTNER
                </h3>
                <div style={{
                  background: '#F8FAFC',
                  padding: '16px',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #22C55E, #16A34A)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontWeight: '700',
                    fontSize: '18px'
                  }}>
                    {(order.deliveryPartnerId?.name || 'D')[0].toUpperCase()}
                  </div>
                  <div>
                    <p style={{ margin: 0, fontWeight: '600', color: '#1E293B' }}>
                      {order.deliveryPartnerId?.name || 'Assigned Partner'}
                    </p>
                    <p style={{ margin: '2px 0 0', fontSize: '14px', color: '#64748B' }}>
                      📞 {order.deliveryPartnerId?.phone || 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '12px', marginTop: '24px', flexWrap: 'wrap' }}>
              {order.status === 'pending' && (
                <>
                  <button
                    onClick={() => { updateOrderStatus(order._id, 'preparing', order); onClose(); }}
                    style={{
                      flex: 1,
                      padding: '14px',
                      border: 'none',
                      borderRadius: '10px',
                      background: '#FF6B6B',
                      color: 'white',
                      fontWeight: '600',
                      fontSize: '14px',
                      cursor: 'pointer'
                    }}
                  >
                    ✓ Accept Order
                  </button>
                  <button
                    onClick={() => { updateOrderStatus(order._id, 'cancelled'); onClose(); }}
                    style={{
                      padding: '14px 20px',
                      border: '1px solid #E2E8F0',
                      borderRadius: '10px',
                      background: 'white',
                      color: '#64748B',
                      fontWeight: '600',
                      fontSize: '14px',
                      cursor: 'pointer'
                    }}
                  >
                    ✕ Reject
                  </button>
                </>
              )}
              {order.status === 'restaurant_confirmed' && (
                <button
                  onClick={() => { updateOrderStatus(order._id, 'preparing', order); onClose(); }}
                  style={{
                    flex: 1,
                    padding: '14px',
                    border: 'none',
                    borderRadius: '10px',
                    background: '#3B82F6',
                    color: 'white',
                    fontWeight: '600',
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  👨‍🍳 Start Preparing
                </button>
              )}
              {order.status === 'preparing' && (
                <button
                  onClick={() => { updateOrderStatus(order._id, 'ready_for_pickup'); onClose(); }}
                  style={{
                    flex: 1,
                    padding: '14px',
                    border: 'none',
                    borderRadius: '10px',
                    background: '#22C55E',
                    color: 'white',
                    fontWeight: '600',
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  📦 Mark as Ready
                </button>
              )}
              {['ready_for_pickup', 'assigned_delivery', 'picked_up', 'on_the_way', 'delivered', 'cancelled'].includes(order.status) && (
                <button
                  onClick={onClose}
                  style={{
                    flex: 1,
                    padding: '14px',
                    border: '1px solid #E2E8F0',
                    borderRadius: '10px',
                    background: 'white',
                    color: '#64748B',
                    fontWeight: '600',
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  Close
                </button>
              )}
              {/* Print Button - Always visible in modal */}
              <button
                onClick={() => printOrder(order, null, false, true)}
                style={{
                  width: '100%',
                  padding: '14px',
                  border: '1px solid #3B82F6',
                  borderRadius: '10px',
                  background: 'white',
                  color: '#3B82F6',
                  fontWeight: '600',
                  fontSize: '14px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  marginTop: '8px'
                }}
              >
                🖨️ Print Order Summary
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) return <div className="loading">🔄 Loading orders...</div>;

  // Filter orders based on active tab
  const getFilteredOrders = () => {
    switch (activeTab) {
      case 'pending':
        return orders.filter(o => o.status === 'pending');
      case 'active':
        return orders.filter(o => ['restaurant_confirmed', 'preparing', 'ready_for_pickup', 'assigned_delivery', 'picked_up', 'on_the_way'].includes(o.status));
      case 'completed':
        return orders.filter(o => ['delivered', 'cancelled'].includes(o.status));
      default:
        return orders;
    }
  };

  const visibleOrders = getFilteredOrders();

  // Count orders for each tab
  const pendingCount = orders.filter(o => o.status === 'pending').length;
  const activeCount = orders.filter(o => ['restaurant_confirmed', 'preparing', 'ready_for_pickup', 'assigned_delivery', 'picked_up', 'on_the_way'].includes(o.status)).length;
  const completedCount = orders.filter(o => ['delivered', 'cancelled'].includes(o.status)).length;

  return (
    <div style={{ background: '#F8FAFC', minHeight: '100vh' }}>
      {/* Audio element for notification sound - always use custom sound, no fallback */}
      <audio
        ref={audioRef}
        preload="auto"
        src={customSoundUrl || CUSTOM_NOTIFICATION_SOUND_URL}
        onError={(e) => {
          console.log('🔊 Audio preload error (will retry on play):', e.target.error?.message || 'Unknown error');
          // Don't fallback - let playNotificationSound handle it with multiple methods
        }}
        onCanPlayThrough={() => console.log('🔊 Audio ready to play (custom sound)')}
        onLoadedData={() => console.log('🔊 Audio loaded:', audioRef.current?.src)}
      />

      {/* New Order Alert Toast */}
      {newOrderAlert && (
        <div style={{
          position: 'fixed',
          top: '100px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1000,
          padding: 'var(--space-lg) var(--space-2xl)',
          background: 'linear-gradient(135deg, #51cf66 0%, #40c057 100%)',
          borderRadius: 'var(--radius-xl)',
          boxShadow: 'var(--shadow-2xl)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-md)',
          animation: 'slideDown 0.3s ease-out'
        }}>
          <span style={{ fontSize: '40px' }}>🆕</span>
          <div>
            <h3 style={{ margin: 0, fontSize: 'var(--font-size-xl)', fontWeight: '800' }}>
              New Order Received!
            </h3>
            <p style={{ margin: 0, fontSize: 'var(--font-size-sm)', opacity: 0.9 }}>
              Order #{newOrderAlert.orderNumber || 'New'} - ₹{newOrderAlert.total || '0'}
            </p>
          </div>
          <button
            onClick={() => setNewOrderAlert(null)}
            style={{
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              borderRadius: '50%',
              width: '32px',
              height: '32px',
              color: 'white',
              cursor: 'pointer',
              marginLeft: 'var(--space-md)'
            }}
          >
            ✕
          </button>
        </div>
      )}

      <div className="container">
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 'var(--space-2xl)' }} className="fade-in">
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 'var(--space-md)', marginBottom: 'var(--space-sm)' }}>
            <h1 style={{
              fontSize: 'var(--font-size-4xl)',
              fontWeight: '800',
              background: 'var(--gradient-primary)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              margin: 0
            }}>
              📦 Orders Management
            </h1>
          </div>
          <p style={{ color: 'var(--gray-600)', fontSize: 'var(--font-size-lg)' }}>
            View and manage incoming orders • Auto-refreshes every 15 seconds • Paid orders auto-confirm 30 seconds after payment ⏱️
          </p>
        </div>

        {/* Tab Navigation - Flutter Style */}
        <div style={{
          display: 'flex',
          gap: '8px',
          marginBottom: 'var(--space-xl)',
          background: 'white',
          padding: '8px',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
        }}>
          {[
            { id: 'pending', label: 'New', count: pendingCount },
            { id: 'active', label: 'Active', count: activeCount },
            { id: 'completed', label: 'Completed', count: completedCount }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                flex: 1,
                padding: '12px 16px',
                border: 'none',
                borderRadius: '8px',
                background: activeTab === tab.id ? '#FF6B6B' : 'white',
                color: activeTab === tab.id ? 'white' : '#64748B',
                fontWeight: '600',
                fontSize: '14px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              <span>{tab.label}</span>
              {tab.count > 0 && (
                <span style={{
                  background: activeTab === tab.id ? 'rgba(255,255,255,0.25)' : '#F1F5F9',
                  padding: '2px 8px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: '700',
                  minWidth: '24px',
                  textAlign: 'center'
                }}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {visibleOrders.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '60px 20px',
            background: 'white',
            borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
          }}>
            <div style={{ fontSize: '80px', marginBottom: '16px', opacity: 0.3 }}>
              {activeTab === 'pending' ? '📋' : activeTab === 'active' ? '👨‍🍳' : '✅'}
            </div>
            <h2 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#64748B',
              marginBottom: '8px'
            }}>
              {activeTab === 'pending' ? 'No pending orders' :
               activeTab === 'active' ? 'No active orders' :
               'No completed orders'}
            </h2>
            <p style={{ color: '#94A3B8', fontSize: '14px', margin: 0 }}>
              {activeTab === 'pending' ? 'New orders will appear here' :
               activeTab === 'active' ? 'Active orders will appear here' :
               'Completed orders will appear here'}
            </p>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '16px' }}>
            {visibleOrders.map(order => (
              <div key={order._id} className="card fade-in" style={{
                position: 'relative',
                overflow: 'hidden',
                background: 'white',
                borderRadius: '12px',
                padding: '20px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                border: '1px solid #E2E8F0',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onClick={() => openOrderDetail(order)}
              onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)'}
              onMouseLeave={(e) => e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.08)'}>
                {/* Status Badge */}
                <div style={{
                  position: 'absolute',
                  top: '16px',
                  right: '16px',
                  padding: '6px 12px',
                  borderRadius: '8px',
                  background: getStatusColor(order.status),
                  color: 'white',
                  fontWeight: '600',
                  fontSize: '11px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  {order.status.replace(/_/g, ' ')}
                </div>

                {/* Order Header */}
                <div style={{ paddingRight: '140px', marginBottom: '16px' }}>
                  <h2 style={{
                    fontSize: '18px',
                    fontWeight: '700',
                    color: '#1E293B',
                    marginBottom: '4px'
                  }}>
                    Order #{order.orderNumber}
                  </h2>
                  <p style={{
                    fontSize: '13px',
                    color: '#64748B',
                    margin: 0
                  }}>
                    {formatOrderTimeIST(order.createdAt)}
                  </p>
                </div>

                {/* Customer Info */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  marginBottom: order.deliveryPartnerId ? '12px' : '16px',
                  paddingBottom: order.deliveryPartnerId ? '12px' : '16px',
                  borderBottom: '1px solid #F1F5F9'
                }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #FF6B6B, #FF8E8E)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontWeight: '700',
                    fontSize: '16px'
                  }}>
                    {(order.userId?.name || 'U')[0].toUpperCase()}
                  </div>
                  <div style={{ flex: 1 }}>
                    <p style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#1E293B',
                      margin: 0,
                      marginBottom: '2px'
                    }}>
                      👤 {order.userId?.name || 'Unknown User'}
                    </p>
                    <p style={{
                      fontSize: '13px',
                      color: '#64748B',
                      margin: 0
                    }}>
                      📞 {order.userId?.phone || 'N/A'}
                    </p>
                  </div>
                </div>

                {/* Delivery Partner Info */}
                {order.deliveryPartnerId && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '16px',
                    paddingBottom: '16px',
                    borderBottom: '1px solid #F1F5F9'
                  }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #22C55E, #16A34A)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontWeight: '700',
                      fontSize: '16px'
                    }}>
                      {(order.deliveryPartnerId?.name || 'D')[0].toUpperCase()}
                    </div>
                    <div style={{ flex: 1 }}>
                      <p style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#1E293B',
                        margin: 0,
                        marginBottom: '2px'
                      }}>
                        🚴 {order.deliveryPartnerId?.name || 'Delivery Partner'}
                      </p>
                      <p style={{
                        fontSize: '13px',
                        color: '#64748B',
                        margin: 0
                      }}>
                        📞 {order.deliveryPartnerId?.phone || 'N/A'}
                      </p>
                    </div>
                  </div>
                )}

                {/* Special Instructions — shown on the card so it's seen at a glance */}
                {order.specialInstructions && (
                  <div style={{ marginBottom: '16px', background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: '8px', padding: '10px 12px' }}>
                    <p style={{ margin: 0, fontSize: '12px', fontWeight: 700, color: '#92400E' }}>📝 Special Instructions</p>
                    <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#92400E', whiteSpace: 'pre-wrap' }}>{order.specialInstructions}</p>
                  </div>
                )}

                {/* Order Items */}
                <div style={{ marginBottom: '16px' }}>
                  {order.items.map((item, index) => (
                    <div
                      key={index}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '8px 0'
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <span style={{
                          fontSize: '14px',
                          color: '#1E293B',
                          fontWeight: '500'
                        }}>
                          {item.name}
                        </span>
                        <span style={{
                          fontSize: '13px',
                          color: '#64748B',
                          marginLeft: '8px'
                        }}>
                          × {item.quantity}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Restaurant Earnings - use netPayable if available, else subtotal + tax */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  paddingTop: '16px',
                  marginBottom: '20px',
                  borderTop: '1px solid #F1F5F9'
                }}>
                  <span style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#64748B'
                  }}>
                    Total
                  </span>
                  <span style={{
                    fontSize: '20px',
                    fontWeight: '700',
                    color: '#FF6B6B'
                  }}>
                    ₹{order.earnings?.restaurantEarnings?.netPayable > 0
                      ? order.earnings.restaurantEarnings.netPayable.toFixed(0)
                      : ((order.pricing?.subtotal || 0) + (order.earnings?.restaurantEarnings?.gstCollected || 0)).toFixed(0)}
                  </span>
                </div>

                {/* Action Buttons */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }} onClick={(e) => e.stopPropagation()}>
                  {order.status === 'pending' && (
                    <div style={{ display: 'flex', gap: '12px' }}>
                      <button
                        onClick={(e) => { e.stopPropagation(); updateOrderStatus(order._id, 'preparing', order); }}
                        onMouseEnter={(e) => e.target.style.background = '#EF4444'}
                        onMouseLeave={(e) => e.target.style.background = '#FF6B6B'}
                        style={{
                          flex: 1,
                          padding: '14px',
                          border: 'none',
                          borderRadius: '8px',
                          background: '#FF6B6B',
                          color: 'white',
                          fontWeight: '600',
                          fontSize: '14px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        Accept
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); updateOrderStatus(order._id, 'cancelled'); }}
                        onMouseEnter={(e) => e.target.style.background = '#F8FAFC'}
                        onMouseLeave={(e) => e.target.style.background = 'white'}
                        style={{
                          padding: '14px 20px',
                          border: '1px solid #E2E8F0',
                          borderRadius: '8px',
                          background: 'white',
                          color: '#64748B',
                          fontWeight: '600',
                          fontSize: '14px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        Reject
                      </button>
                    </div>
                  )}
                  {order.status === 'restaurant_confirmed' && (
                    <button
                      onClick={(e) => { e.stopPropagation(); updateOrderStatus(order._id, 'preparing', order); }}
                      onMouseEnter={(e) => e.target.style.background = '#2563EB'}
                      onMouseLeave={(e) => e.target.style.background = '#3B82F6'}
                      style={{
                        width: '100%',
                        padding: '14px',
                        border: 'none',
                        borderRadius: '8px',
                        background: '#3B82F6',
                        color: 'white',
                        fontWeight: '600',
                        fontSize: '14px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      Start Preparing
                    </button>
                  )}
                  {order.status === 'preparing' && (
                    <button
                      onClick={(e) => { e.stopPropagation(); updateOrderStatus(order._id, 'ready_for_pickup'); }}
                      onMouseEnter={(e) => e.target.style.background = '#16A34A'}
                      onMouseLeave={(e) => e.target.style.background = '#22C55E'}
                      style={{
                        width: '100%',
                        padding: '14px',
                        border: 'none',
                        borderRadius: '8px',
                        background: '#22C55E',
                        color: 'white',
                        fontWeight: '600',
                        fontSize: '14px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      📦 Mark as Ready
                    </button>
                  )}
                  {/* Print Order Button - Available for all orders */}
                  <button
                    onClick={(e) => printOrder(order, e, false, true)}
                    onMouseEnter={(e) => e.target.style.background = '#F1F5F9'}
                    onMouseLeave={(e) => e.target.style.background = 'white'}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #E2E8F0',
                      borderRadius: '8px',
                      background: 'white',
                      color: '#64748B',
                      fontWeight: '600',
                      fontSize: '14px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px'
                    }}
                  >
                    🖨️ Print Order
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Order Detail Modal */}
      {showOrderDetail && selectedOrder && (
        <OrderDetailModal order={selectedOrder} onClose={closeOrderDetail} />
      )}

      {/* Toast Notifications Container */}
      {toasts.length > 0 && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          zIndex: 1001,
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          maxWidth: '400px'
        }}>
          {toasts.map((toast) => (
            <div
              key={toast.id}
              style={{
                padding: '14px 18px',
                borderRadius: '12px',
                background: toast.type === TOAST_TYPES.SUCCESS ? '#10B981' :
                            toast.type === TOAST_TYPES.WARNING ? '#F59E0B' :
                            toast.type === TOAST_TYPES.ERROR ? '#EF4444' : '#3B82F6',
                color: 'white',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                animation: 'slideInRight 0.3s ease-out',
                cursor: 'pointer'
              }}
              onClick={() => removeToast(toast.id)}
            >
              <span style={{ fontSize: '18px' }}>
                {toast.type === TOAST_TYPES.SUCCESS ? '✅' :
                 toast.type === TOAST_TYPES.WARNING ? '⚠️' :
                 toast.type === TOAST_TYPES.ERROR ? '❌' : 'ℹ️'}
              </span>
              <span style={{ flex: 1, fontSize: '14px', fontWeight: '500' }}>
                {toast.message}
              </span>
              <button
                onClick={(e) => { e.stopPropagation(); removeToast(toast.id); }}
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  border: 'none',
                  borderRadius: '50%',
                  width: '24px',
                  height: '24px',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Toast Animation Styles */}
      <style>{`
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
}

export default RestaurantOrders;
